import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Sparkles, ArrowRight, Share2, Facebook, Mail, School, Quote, ArrowLeft, GraduationCap, Users, User } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api'; 

const WhatsappIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
);

const testimonials = [
  { text: "Preparé una clase de Historia en 5 minutos. Los alumnos estuvieron enganchados todo el módulo.", author: "Prof. María González", school: "Colegio San Martín" },
  { text: "Es el motor que siempre necesité para organizar mis secuencias didácticas de forma profesional.", author: "Prof. Ricardo Sosa", school: "Instituto Don Bosco" },
  { text: "Logré que mis alumnos de secundaria participen activamente gracias a las dinámicas de gamificación.", author: "Prof. Lucía Méndez", school: "Colegio Santa María" },
  { text: "Optimicé mi tiempo de planificación en un 80%. Ahora me centro en lo que importa: enseñar.", author: "Prof. Javier López", school: "Escuela Técnica N°1" }
];

const TEACHING_LEVELS = ['Inicial', 'Primaria', 'Secundaria', 'Superior / Universitario'];

function formatError(detail) {
  if (!detail) return 'Algo salió mal. Intentá de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function AuthPage() {
  const [mode, setMode] = useState('login'); 
  const [registerStep, setRegisterStep] = useState(1); 
  const [isInstitutional, setIsInstitutional] = useState(false); 
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [inviteSlug, setInviteSlug] = useState(''); 
  const [age, setAge] = useState(''); 
  const [levels, setLevels] = useState([]); 
  const [subjects, setSubjects] = useState(''); 
  
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  
  const { login, register, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlInvite = searchParams.get('invite');

  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (urlInvite) {
      setMode('register');
      setIsInstitutional(true);
      setInviteSlug(urlInvite);
    }
  }, [urlInvite]);

  const toggleLevel = (lvl) => {
    setLevels(prev => prev.includes(lvl) ? prev.filter(l => l !== lvl) : [...prev, lvl]);
  };

  const handleStepOne = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) {
      setError('Por favor, completá los datos básicos.');
      return false;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return false;
    }
    
    if (isInstitutional) {
      if (!inviteSlug.trim()) {
        setError('Ingresá el código de tu escuela.');
        return false;
      }
      setLoading(true);
      try {
        await api.get(`/invite/${inviteSlug.trim()}`);
        setLoading(false);
        return true; // <--- ERROR CORREGIDO ACÁ (Era True en vez de true)
      } catch (err) {
        setLoading(false);
        setError(formatError(err.response?.data?.detail) || 'Código de escuela inválido.');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'register' && registerStep === 1) {
      const isValid = await handleStepOne();
      if (isValid) setRegisterStep(2);
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        navigate('/');
      } else if (mode === 'register' && registerStep === 2) {
        const subjectsArray = subjects.split(',').map(s => s.trim()).filter(s => s);
        await register(
          email, 
          password, 
          name, 
          isInstitutional ? inviteSlug.trim() : null,
          age, 
          levels, 
          subjectsArray
        );
        navigate('/');
      } else if (mode === 'forgot') {
        const res = await forgotPassword(email);
        toast.success(res.message || 'Instrucciones enviadas al correo.');
        setMode('login'); 
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
      if (mode === 'register') setRegisterStep(1); 
    } finally {
      setLoading(false);
    }
  };

  const shareApp = (platform) => {
    const url = "https://dinamiclassapp.com.ar";
    const text = "¡Profe, tenés que probar DinamiClass! Excelente motor para organizar dinámicas de clase en segundos. 🚀";
    if (platform === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, '_blank');
    if (platform === 'facebook') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success('Enlace copiado');
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex flex-col lg:flex-row">
      
      <div className="flex flex-col w-full lg:w-1/2 relative overflow-hidden justify-between p-8 lg:p-12 min-h-[50vh] lg:min-h-screen z-10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/3d891077-d4d4-4c86-bf60-f7dc6784f6ce/images/8ffaf283f89bb2eabf9ca720024cd9d7b7e9a11d843469bc11123dfa3e051ac4.png)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/80 to-blue-950/60" />

        <div className="relative flex items-center mb-6 lg:mb-0 animate-fade-in">
          <img src="/logo.png" alt="DinamiClass" className="h-20 lg:h-28 w-auto object-contain origin-left drop-shadow-2xl" />
        </div>

        <div className="relative space-y-4 lg:space-y-6">
          <p className="text-[10px] lg:text-xs text-blue-400 uppercase tracking-[0.2em] font-bold">Plataforma de Optimización Didáctica</p>
          
          <h1 className="text-3xl lg:text-5xl font-black text-white leading-[1.1] tracking-tight">
            Transformá tus clases en<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">experiencias únicas</span>
          </h1>
          
          <p className="text-slate-300 text-sm lg:text-lg leading-relaxed max-w-md font-medium">
            Algoritmo avanzado que procesa cualquier tema en actividades listas para usar en el aula.
          </p>

          <div className="grid grid-cols-3 gap-3 pt-2 lg:pt-4">
            {[
              { value: '3', label: 'Estructuras' },
              { value: '100%', label: 'Enfoque Docente' },
              { value: 'Sec.', label: 'Procesamiento' },
            ].map((s, i) => (
              <div key={i} className="bg-slate-900/50 backdrop-blur-md border border-white/10 rounded-xl p-3 text-center shadow-lg">
                <p className="text-2xl lg:text-3xl font-black text-blue-400">{s.value}</p>
                <p className="text-[9px] lg:text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mt-6 flex flex-col justify-center shadow-2xl transition-all duration-500 overflow-hidden">
          <Quote className="absolute top-3 right-5 text-white/5" size={60} />
          <div key={testimonialIndex} className="relative z-10 animate-fade-in">
            <p className="text-slate-200 text-sm italic leading-relaxed font-medium">
              "{testimonials[testimonialIndex].text}"
            </p>
            <div className="flex items-center gap-3 mt-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-black text-sm shadow-lg border-2 border-white/10">
                {testimonials[testimonialIndex].author.split(' ')[1][0]}
              </div>
              <div>
                <p className="text-white text-xs font-bold">{testimonials[testimonialIndex].author}</p>
                <p className="text-blue-400 text-[10px] font-semibold uppercase tracking-wider">{testimonials[testimonialIndex].school}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-8 relative z-20 bg-[#030712]">
        <div className="w-full max-w-md animate-fade-in-up">
          
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-[2rem] p-6 lg:p-8 relative overflow-hidden shadow-2xl">
            
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-800/50">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.15em]">Compartir plataforma:</span>
                <div className="flex gap-2">
                    <button type="button" onClick={() => shareApp('whatsapp')} className="p-2 bg-emerald-500/10 text-emerald-500 rounded-xl hover:bg-emerald-500/20 transition-colors">
                        <WhatsappIcon size={16} />
                    </button>
                    <button type="button" onClick={() => shareApp('facebook')} className="p-2 bg-blue-500/10 text-blue-500 rounded-xl hover:bg-blue-500/20 transition-colors">
                        <Facebook size={16} />
                    </button>
                    <button type="button" onClick={() => shareApp('copy')} className="p-2 bg-slate-800 text-slate-400 rounded-xl hover:text-white transition-colors">
                        <Share2 size={16} />
                    </button>
                </div>
            </div>

            {mode !== 'forgot' && registerStep === 1 && (
              <div className="flex bg-slate-950 rounded-xl p-1 mb-5 border border-slate-800">
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${mode === 'login' ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  Iniciar Sesión
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('register'); setError(''); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${mode === 'register' ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  Registrarse
                </button>
              </div>
            )}

            <div className="mb-5">
              {registerStep === 2 && (
                <button onClick={() => setRegisterStep(1)} className="text-slate-500 hover:text-white mb-4 transition-colors flex items-center gap-1 text-xs font-bold">
                  <ArrowLeft size={14} /> Volver
                </button>
              )}
              <h2 className="text-2xl font-black text-white tracking-tight">
                {mode === 'login' ? 'Bienvenido de vuelta' 
                  : mode === 'register' && registerStep === 1 ? 'Creá tu cuenta' 
                  : mode === 'register' && registerStep === 2 ? 'Personalizá tu perfil' 
                  : 'Recuperar clave'}
              </h2>
              <p className="text-slate-400 text-xs mt-1 font-medium">
                {mode === 'login' ? 'Ingresá tus credenciales para continuar' 
                  : mode === 'register' && registerStep === 1 ? 'Empezá hoy con 10 procesamientos gratuitos' 
                  : mode === 'register' && registerStep === 2 ? 'Contanos sobre vos para adaptar las dinámicas' 
                  : 'Te enviaremos un enlace para cambiarla'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              
              {((mode === 'register' && registerStep === 1) || mode !== 'register') && (
                <>
                  {mode === 'register' && (
                    <div className="animate-fade-in space-y-4">
                      
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => setIsInstitutional(false)}
                          className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                            !isInstitutional 
                            ? 'bg-blue-600/10 border-blue-500 text-blue-400 shadow-inner' 
                            : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'
                          }`}
                        >
                          <User size={18} className="mb-1" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Docente</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsInstitutional(true)}
                          className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                            isInstitutional 
                            ? 'bg-emerald-600/10 border-emerald-500 text-emerald-400 shadow-inner' 
                            : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'
                          }`}
                        >
                          <School size={18} className="mb-1" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Institución</span>
                        </button>
                      </div>

                      {isInstitutional && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 animate-fade-in-up">
                          <label className="block text-[10px] font-bold text-emerald-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                            <School size={12} /> Código del Colegio
                          </label>
                          <input
                            type="text"
                            value={inviteSlug}
                            onChange={e => setInviteSlug(e.target.value)}
                            placeholder="Ej: colegio-san-martin"
                            required={isInstitutional}
                            className="w-full bg-slate-950 border border-emerald-500/50 rounded-lg px-3 py-2 text-white placeholder-slate-600 focus:ring-1 focus:ring-emerald-500 outline-none text-sm font-medium font-mono"
                          />
                          <p className="text-[9px] text-emerald-500/70 mt-1.5 leading-tight">Pedile a tu director el código exacto para asociar tu cuenta y recibir tu licencia premium.</p>
                        </div>
                      )}

                      <div className="space-y-1 mt-2">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">Nombre completo</label>
                        <input
                          type="text"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          placeholder="Prof. Ana García"
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all outline-none text-sm font-medium"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider ml-1">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder={mode === 'register' && isInstitutional ? "tu.correo@colegio.edu.ar" : "ejemplo@gmail.com"}
                      required
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all outline-none text-sm font-medium"
                    />
                  </div>

                  {mode !== 'forgot' && (
                    <div className="space-y-1">
                      <div className="flex justify-between items-center ml-1 mb-1">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Contraseña</label>
                          {mode === 'login' && (
                              <button 
                                  type="button"
                                  onClick={() => { setMode('forgot'); setError(''); }}
                                  className="text-[10px] font-bold text-blue-400 hover:text-blue-300 transition-colors"
                              >
                                  ¿Olvidaste tu clave?
                              </button>
                          )}
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder={mode === 'register' ? 'Mínimo 6 caracteres' : '••••••••'}
                          required={mode !== 'forgot'}
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 pr-12 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all outline-none text-sm font-medium"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {mode === 'register' && registerStep === 2 && (
                <div className="space-y-5 animate-fade-in">
                  
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-300 ml-1">
                      <Users size={14} className="text-blue-400" /> Tu Edad (Opcional)
                    </label>
                    <input
                      type="number"
                      value={age}
                      onChange={e => setAge(e.target.value)}
                      placeholder="Ej: 35"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all outline-none text-sm font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-300 ml-1">
                      <School size={14} className="text-blue-400" /> ¿En qué niveles das clase?
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {TEACHING_LEVELS.map(lvl => (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => toggleLevel(lvl)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                            levels.includes(lvl) 
                              ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-500/20' 
                              : 'bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-500'
                          }`}
                        >
                          {lvl}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-300 ml-1">
                      <GraduationCap size={14} className="text-blue-400" /> ¿Qué materias enseñás?
                    </label>
                    <input
                      type="text"
                      value={subjects}
                      onChange={e => setSubjects(e.target.value)}
                      placeholder="Ej: Matemáticas, Biología, Historia..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all outline-none text-sm font-medium"
                    />
                    <p className="text-[10px] text-slate-500 ml-1">Separá las materias con comas.</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-2.5 text-red-400 text-xs font-semibold leading-relaxed animate-fade-in">
                  ⚠️ {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-black uppercase tracking-widest text-sm rounded-xl py-3 shadow-lg shadow-blue-900/20 transition-all flex items-center justify-center gap-2 mt-4 active:scale-[0.98]"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : mode === 'forgot' ? (
                  <>
                    <Mail size={16} strokeWidth={2} /> Enviar enlace
                  </>
                ) : mode === 'register' && registerStep === 1 ? (
                  <>
                    Siguiente paso <ArrowRight size={16} strokeWidth={2.5} />
                  </>
                ) : (
                  <>
                    <Sparkles size={16} strokeWidth={2} />
                    {mode === 'login' ? 'Ingresar' : 'Crear Cuenta y Empezar'}
                  </>
                )}
              </button>

              {mode === 'forgot' && (
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="w-full text-center text-xs font-bold text-slate-500 hover:text-slate-300 transition-colors mt-3"
                >
                  Volver al inicio de sesión
                </button>
              )}

              {mode === 'register' && registerStep === 1 && (
                <p className="text-center text-[9px] font-medium text-slate-500 mt-3 leading-relaxed">
                  Al registrarte, aceptás los{' '}
                  <button type="button" onClick={() => window.open('/legal#terms', '_blank')} className="text-blue-400 hover:underline">
                    Términos de Servicio
                  </button>{' '}
                  y la{' '}
                  <button type="button" onClick={() => window.open('/legal#privacy', '_blank')} className="text-blue-400 hover:underline">
                    Política de Privacidad
                  </button>.
                </p>
              )}
            </form>
          </div>

          <div className="mt-5 flex justify-center">
              <a 
                href="mailto:soporte@dinamiclassapp.com.ar"
                className="text-slate-600 hover:text-slate-400 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors"
              >
                  ¿Necesitás ayuda técnica?
              </a>
          </div>
        </div>
      </div>
    </div>
  );
}
